/*
=============================================
   CONVERT  (professor portal view)
=============================================
Upload a legacy exercise workbook, convert it for the add-in, seed the database,
and store the file for student download.

WHY THIS IS A COMPONENT AND NOT A PAGE. Convert used to live in its own
standalone convert.html, reached by a full page navigation, while every other
menu item is a hash view rendered inside <app-shell>. The visible cost was the
sidebar disappearing whenever you converted something, which is disorienting in a
tool you are meant to move around in. The hidden cost was worse: a second page
meant a second copy of the auth gate, the SERVER constant, and the base styling,
which is three things to keep in step with the rest of the portal forever.

The route handling is unchanged. This posts to the same /convert and
/convert-batch endpoints with the same fields.

convert.html is kept as a redirect so existing bookmarks and the sideload guide
still land somewhere sensible.
*/

// SERVER comes from api.js rather than being redefined here. A second copy of
// the host is exactly the kind of duplication that made convert.html drift.
import { api, SERVER } from "../scripts/api.js";

function b64ToBlobUrl(b64, type) {
   const bin = atob(b64);
   const arr = new Uint8Array(bin.length);
   for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
   return URL.createObjectURL(new Blob([arr], {
      type: type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
   }));
}

function download(filename, b64) {
   const url = b64ToBlobUrl(b64);
   const a = document.createElement("a");
   a.href = url;
   a.download = filename;
   a.click();
   URL.revokeObjectURL(url);
}

class AppConvert extends HTMLElement {
   connectedCallback() {
      this.innerHTML = `
         <h1 class="page-title">Convert an exercise</h1>
         <p class="page-sub">Adds the add-in sheets, repoints the answer colouring and
            disconnect warning, appends the student instructions, seeds the database,
            and stores the file for student download.</p>

         <div id="cv-gate" class="cv-card">Checking access…</div>

         <div id="cv-app" hidden>
            <div class="cv-tabs">
               <button type="button" class="cv-tab on" data-tab="single">Single file</button>
               <button type="button" class="cv-tab" data-tab="batch">Bulk (zip + spec)</button>
            </div>

            <form id="cv-single" class="cv-card">
               <label>Exercise file <span class="hint">(.xlsm)</span></label>
               <input type="file" name="file" accept=".xlsm,.xlsx" required />
               <div class="cv-row">
                  <div><label>Title</label>
                     <input type="text" name="title" placeholder="e.g. Keyboarding Exercises" /></div>
                  <div><label>Assignment code <span class="hint">(blank = generate)</span></label>
                     <input type="text" name="code" placeholder="auto" /></div>
               </div>
               <div class="cv-row">
                  <div><label>Assignment ID <span class="hint">(blank = next)</span></label>
                     <input type="number" name="assignment_id" placeholder="auto" /></div>
                  <div><label>Course ID</label>
                     <input type="number" name="course_id" placeholder="optional" /></div>
               </div>
               <div class="cv-row">
                  <div><label>Feedback</label>
                     <select name="feedback"><option>Live</option><option>Static</option></select></div>
                  <div><label>Grading</label>
                     <select name="grading_location">
                        <option value="client">client (local)</option>
                        <option value="server">server (Path B)</option>
                     </select>
                     <span class="hint" id="cv-grading-hint" hidden>Forced to server:
                        a stripped file keeps no answer key to grade against
                        locally.</span></div>
               </div>
               <label>Workbook password
                  <span class="hint">(from the Password Master List; stored with the file)</span></label>
               <input type="text" name="password" placeholder="optional" />
               <label class="cv-check"><input type="checkbox" name="production" />
                  Production build (hide add-in tabs)</label>
               <label class="cv-check"><input type="checkbox" name="seed" checked />
                  Seed the database + reload (uncheck to just convert)</label>
               <label class="cv-check"><input type="checkbox" name="strip" />
                  Server-only parameters (delete the answer + RandVar tables from the
                  student file). Leave OFF for legacy files that use their own in-file
                  Reset Parameters.</label>
               <button class="cv-go" type="submit">Convert</button>
               <div class="cv-msg" id="cv-single-msg"></div>
            </form>

            <form id="cv-batch" class="cv-card" hidden>
               <label>Files <span class="hint">(a .zip of .xlsm files)</span></label>
               <input type="file" name="bundle" accept=".zip" required />
               <label>Spec sheet <span class="hint">(.xlsx —
                  <a id="cv-tmpl" href="/assets/Batch-Spec-Template.xlsx">download template</a>)</span></label>
               <input type="file" name="spec" accept=".xlsx" required />
               <button class="cv-go" type="submit">Convert batch</button>
               <div class="cv-msg" id="cv-batch-msg"></div>
               <div id="cv-batch-results"></div>
            </form>
         </div>`;

      this.gate();
      this.wireTabs();
      // Ticking "server-only parameters" strips the answer key out of the student
      // file, which makes client grading impossible rather than merely different.
      // The server derives grading_location from the strip regardless, so this is
      // only here to stop the form from PRESENTING an unreachable choice. Both
      // halves are needed: the form so the professor is never misled, the server so
      // a stale form, the batch endpoint or a direct API call cannot reintroduce it.
      const strip = this.querySelector('#cv-single [name="strip"]');
      const grading = this.querySelector('#cv-single [name="grading_location"]');
      const gradingHint = this.querySelector("#cv-grading-hint");
      if (strip && grading) {
         const syncGrading = () => {
            if (strip.checked) {
               grading.dataset.prev = grading.value;
               grading.value = "server";
               grading.disabled = true;           // a disabled select is not submitted
            } else {
               grading.disabled = false;
               if (grading.dataset.prev) grading.value = grading.dataset.prev;
            }
            if (gradingHint) gradingHint.hidden = !strip.checked;
         };
         strip.addEventListener("change", syncGrading);
         syncGrading();
      }

      this.querySelector("#cv-single").addEventListener("submit", (e) => this.single(e));
      this.querySelector("#cv-batch").addEventListener("submit", (e) => this.batch(e));
   }

   msg(sel, text, kind) {
      const el = this.querySelector(sel);
      if (!el) return;
      el.textContent = text;
      el.className = "cv-msg show " + (kind || "");
   }

   /*
   Access gate.

   The server enforces this too (requireProfessor on /convert), so this is about
   telling someone WHY the form is not there rather than about security. A student
   who reaches the URL should read a sentence, not a silent empty panel.
   */
   async gate() {
      const gate = this.querySelector("#cv-gate");
      try {
         const r = await api("/me");
         if (r.status === 401) { gate.textContent = "Please sign in to convert."; return; }
         const me = await r.json();
         if (!me.ok) { gate.textContent = "Please sign in to convert."; return; }
         if (!["professor", "admin"].includes(me.role)) {
            gate.textContent =
               `Signed in as ${me.email}, but converting is for professors and admins.`;
            return;
         }
         gate.hidden = true;
         this.querySelector("#cv-app").hidden = false;
      } catch (err) {
         gate.textContent = "Could not reach the server. Try again.";
      }
   }

   wireTabs() {
      this.querySelectorAll(".cv-tab").forEach((t) => {
         t.addEventListener("click", () => {
            this.querySelectorAll(".cv-tab").forEach((x) => x.classList.remove("on"));
            t.classList.add("on");
            const which = t.dataset.tab;
            this.querySelector("#cv-single").hidden = which !== "single";
            this.querySelector("#cv-batch").hidden = which !== "batch";
         });
      });
   }

   async single(e) {
      e.preventDefault();
      const form = e.target;
      const btn = form.querySelector("button[type=submit]");
      btn.disabled = true;
      this.msg("#cv-single-msg", "Converting…", "");
      try {
         const fd = new FormData(form);
         // Unchecked checkboxes are absent from FormData entirely, so each one is
         // set explicitly: the server needs "false", not "missing".
         fd.set("production", form.production.checked ? "true" : "false");
         fd.set("strip", form.strip.checked ? "true" : "false");
         fd.set("seed", form.seed.checked ? "true" : "false");
         // A DISABLED select is omitted from FormData for the same reason an
         // unchecked box is, so send it by hand. Without this the field arrives
         // missing, the route defaults it to "client", and only the server-side
         // derivation saves it. Belt and braces, and it keeps the request honest.
         if (form.strip.checked) fd.set("grading_location", "server");

         const r = await fetch(`${SERVER}/convert`, {
            method: "POST", body: fd, credentials: "include",
         });
         const j = await r.json();
         if (!j.ok) {
            this.msg("#cv-single-msg", `Error: ${j.reason || ""} ${j.detail || ""}`, "err");
            return;
         }
         download(j.filename, j.file_b64);

         // Report WHAT was detected, not just a question count. A timed challenge
         // legitimately has zero graded questions because it grades itself in the
         // workbook, and a bare "0 questions" reads like a failed conversion when it
         // is the correct answer.
         const seeded = j.seeded ? ` · seeded as assignment ${j.assignment_id}` : " · not seeded";
         const what = j.archetype === "timed_challenge"
            ? `timed challenge · ${j.n_questions} graded questions`
              + (j.n_runs ? ` · ${j.n_runs} past runs in file` : "")
            : `${j.n_questions} questions`;
         // The scoreboard is now built on every upload, derived from the file's own
         // tabs. Say so, and say when it did NOT happen: a board that quietly failed
         // to build is invisible until someone opens the scoreboard weeks later, which
         // is how the portal path went from launch to August never creating one at all.
         const board = !j.seeded ? ""
            : j.scoreboard_sections
               ? ` · scoreboard: ${j.scoreboard_sections} section${j.scoreboard_sections === 1 ? "" : "s"}`
               : ` · NO scoreboard (${j.scoreboard_note || "nothing to group"})`;
         // Only ever appears if the form let an unreachable combination through, so
         // it is a signal that the form and the server have drifted apart.
         const grading = j.grading_note ? ` · ${j.grading_note}` : "";
         // WHAT THE STORED FILE MEASURABLY CONTAINS, said here, in the room, while
         // the professor still has the source open. On 2026-08-28 assignment 45
         // went into a live class with no team conditional-formatting rules and
         // every side of the system reported success; the only symptom was that
         // students never saw a border, days later. A warning at upload time is the
         // cheapest possible moment to catch that.
         const warnings = Array.isArray(j.build_warnings) ? j.build_warnings : [];
         this.msg("#cv-single-msg",
            `Done: ${j.filename} (${what}, code ${j.code}${seeded}${board}${grading}). `
            + "Download started."
            + (warnings.length ? "  ⚠️ " + warnings.join("  ⚠️ ") : ""),
            warnings.length ? "warn" : "ok");
      } catch (err) {
         this.msg("#cv-single-msg", "Request failed: " + err.message, "err");
      } finally {
         btn.disabled = false;
      }
   }

   async batch(e) {
      e.preventDefault();
      const form = e.target;
      const btn = form.querySelector("button[type=submit]");
      btn.disabled = true;
      this.msg("#cv-batch-msg", "Converting batch…", "");
      this.querySelector("#cv-batch-results").innerHTML = "";
      try {
         const r = await fetch(`${SERVER}/convert-batch`, {
            method: "POST", body: new FormData(form), credentials: "include",
         });
         const j = await r.json();
         if (!j.ok) {
            this.msg("#cv-batch-msg", `Error: ${j.reason || ""} ${j.detail || ""}`, "err");
            return;
         }
         const okN = j.results.filter((x) => x.ok).length;
         this.msg("#cv-batch-msg", `${okN} of ${j.results.length} converted.`,
            okN === j.results.length ? "ok" : "err");

         const esc = (t) => String(t ?? "").replace(/[<>&]/g, "");
         const rows = j.results.map((x) => {
            // A batch row that converted and seeded but produced a file that cannot
            // do its job is NOT plain "ok". Twenty files scroll past in one screen
            // and the single broken one has to say so on its own row: nobody
            // re-reads a batch table looking for trouble it did not announce.
            const warns = Array.isArray(x.build_warnings) ? x.build_warnings : [];
            const status = x.ok
               ? (warns.length
                  ? `<span class="pill warn" title="${esc(warns.join(" | "))}">ok, ${warns.length} warning${warns.length === 1 ? "" : "s"}</span>`
                  : `<span class="pill ok">ok</span>`)
               : `<span class="pill bad">${esc(x.error || "error")}</span>`;
            const kind = x.archetype === "timed_challenge" ? "timed challenge" : "assignment";
            const dl = (x.ok && x.download_url)
               ? `<a href="${SERVER}${x.download_url}" target="_blank">download</a>` : "";
            const warnRow = warns.length
               ? `<tr><td colspan="6" class="cv-row-warn">⚠️ ${warns.map(esc).join("<br>⚠️ ")}</td></tr>`
               : "";
            return `<tr><td>${esc(x.filename)}</td><td>${status}</td><td>${x.ok ? kind : ""}</td>`
                 + `<td>${esc(x.code)}</td><td>${esc(x.assignment_id)}</td><td>${dl}</td></tr>`
                 + warnRow;
         }).join("");
         this.querySelector("#cv-batch-results").innerHTML =
            `<table><tr><th>File</th><th>Status</th><th>Type</th><th>Code</th>`
            + `<th>Assignment</th><th></th></tr>${rows}</table>`;
      } catch (err) {
         this.msg("#cv-batch-msg", "Request failed: " + err.message, "err");
      } finally {
         btn.disabled = false;
      }
   }
}

customElements.define("app-convert", AppConvert);
